/**
 * TimelineWindowFilter — element windowing for preview timeline virtualization.
 *
 * Filters a post-layout element list to only those overlapping a computed
 * window around the current playhead. Two modes:
 *
 *  - **Playback**: forward-only walk with element-count budget.
 *    Same-time groups are all-or-nothing.
 *  - **Scrubbing**: symmetric time-based window (no budget cap).
 *
 * @module PLAT-9562 Phase 1
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PlaybackWindowConfig {
  /**
   * Max budget for the visible playback window, measured in cost units.
   * Costs are per element type (see `ELEMENT_TYPE_COSTS`): video=1,
   * image/audio=0.5, text/shape=0.1; types not listed (incl. plain
   * composition) fall back to `DEFAULT_ELEMENT_COST` = 1. Default 10
   * ≈ 10 video-equivalents.
   */
  maxElements: number;
  /** Seconds before timeB at which to trigger re-calculation */
  recheckThreshold: number;
}

export interface ScrubbingWindowConfig {
  /** Seconds before and after playhead to include */
  scrubbingRadius: number;
}

/**
 * Feature flag shape for timeline windowing configuration.
 * Use this as the schema for the JSON feature flag.
 */
export interface TimelineWindowingFeatureFlag {
  enabled?: boolean;
  playback?: Partial<PlaybackWindowConfig>;
  scrubbing?: Partial<ScrubbingWindowConfig>;
}

/** The time range of the current windowing result. */
export interface WindowBounds {
  /** Start of the visible window (seconds) */
  timeA: number;
  /** End of the visible window (seconds) */
  timeB: number;
}

export interface WindowResult extends WindowBounds {
  /** Elements to include in the serialized JSON */
  visibleElements: Record<string, any>[];
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

export const DEFAULT_PLAYBACK_CONFIG: PlaybackWindowConfig = {
  maxElements: 10,
  recheckThreshold: 2,
};

export const DEFAULT_SCRUBBING_CONFIG: ScrubbingWindowConfig = {
  scrubbingRadius: 5,
};

/**
 * Cost contribution of each element type to the playback-window budget.
 * Tuned so video=1 (the heavy reference); image/audio cost half; text/shape
 * are near-free. Unknown types fall back to `DEFAULT_ELEMENT_COST`.
 */
export const ELEMENT_TYPE_COSTS: Readonly<Record<string, number>> = {
  video: 1,
  image: 0.5,
  audio: 0.5,
  text: 0.1,
  shape: 0.1,
};

/** Fallback cost for types absent from `ELEMENT_TYPE_COSTS` (incl. plain composition). */
export const DEFAULT_ELEMENT_COST = 1;

/** Resolve the budget cost of a single element type. */
export function elementCost(type: string | undefined): number {
  if (!type) return DEFAULT_ELEMENT_COST;
  return ELEMENT_TYPE_COSTS[type] ?? DEFAULT_ELEMENT_COST;
}

/**
 * Float tolerance for budget comparisons. Per-type costs include 0.1 (text,
 * shape), which is not exactly representable in IEEE 754 — summing several
 * of them drifts above what the user wrote literally. Treat
 * `totalCost ≤ maxElements + BUDGET_EPSILON` as "fits".
 */
const BUDGET_EPSILON = 1e-9;

/** Configuration for timeline windowing, set via `setWindowingConfig`. */
export interface WindowingConfig {
  enabled: boolean;
  playback: PlaybackWindowConfig;
  scrubbing: ScrubbingWindowConfig;
}

export const DEFAULT_WINDOWING_CONFIG: WindowingConfig = {
  enabled: false,
  playback: DEFAULT_PLAYBACK_CONFIG,
  scrubbing: DEFAULT_SCRUBBING_CONFIG,
};

/**
 * Merges feature flag overrides with default windowing configuration.
 *
 * @param featureFlag - Optional feature flag containing partial config overrides
 * @returns Complete windowing configuration with defaults filled in
 *
 * @example
 * ```typescript
 * const flags = useFeatureFlags();
 * const config = mergeWindowingConfig(flags.timelineWindowingConfig);
 * ```
 */
export function mergeWindowingConfig(
  featureFlag?: TimelineWindowingFeatureFlag,
): WindowingConfig {
  if (!featureFlag) {
    return DEFAULT_WINDOWING_CONFIG;
  }

  return {
    enabled: featureFlag.enabled ?? DEFAULT_WINDOWING_CONFIG.enabled,
    playback: {
      ...DEFAULT_PLAYBACK_CONFIG,
      ...featureFlag.playback,
    },
    scrubbing: {
      ...DEFAULT_SCRUBBING_CONFIG,
      ...featureFlag.scrubbing,
    },
  };
}

/**
 * Whether `bounds` spans `time` — plain containment, nothing more.
 *
 * The question this answers is "does the renderer already hold this position?",
 * which is what a seek or a Play needs to know before commanding the renderer
 * (PLAT-11119).
 *
 * Deliberately distinct from the stores' `needsWindowingRefresh`, which is also
 * true within `recheckThreshold` of `timeB`. That clause is a *look-ahead*, so
 * playback refreshes before running off the end of the window; it says nothing
 * about what the renderer holds. Using it as a coverage test does not converge:
 * a rebuild sets `timeA = time`, so any window narrower than the threshold
 * satisfies it again immediately and the caller rebuilds forever.
 *
 * Absent bounds mean nothing has been windowed yet, so nothing is covered.
 */
export function windowCoversTime(
  bounds: WindowBounds | undefined,
  time: number,
): boolean {
  if (!bounds) return false;
  return time >= bounds.timeA && time <= bounds.timeB;
}

/**
 * The playhead to build a window around, never past the timeline's last frame.
 *
 * Playback stops with the playhead on the timeline duration — `setTime` snaps
 * it there once it is within half a frame — but every element ends at or
 * before that instant, and the window excludes what ends exactly on it. The
 * result is an empty window, which reaches the renderer as a document with no
 * elements: the preview goes black over the final frame while the transport
 * still shows it. An outro card makes it obvious, being the only thing left at
 * the end for the window to lose.
 *
 * Windowing the last frame instead keeps whatever is on screen there. Times
 * inside the timeline, and sources that declare no duration or frame rate,
 * pass through untouched.
 */
export function clampToLastFrame(
  time: number,
  source: { duration?: number | null; frame_rate?: number },
): number {
  const { duration, frame_rate: frameRate } = source;
  if (!duration || !frameRate) return time;
  return Math.min(time, Math.max(0, duration - 1 / frameRate));
}

/** Callbacks for resolving playback state from the host store. */
export interface PlaybackCallbacks {
  getCurrentTime: () => number;
  getIsPlaying: () => boolean;
  getIsScrubbing: () => boolean;
}

// ---------------------------------------------------------------------------
// Core entry point
// ---------------------------------------------------------------------------

/**
 * Returns the subset of `allElements` that fall within a window around
 * `currentTime`, plus the `[timeA, timeB]` bounds of that window.
 */
export function collectWindowElements(
  allElements: Record<string, any>[],
  currentTime: number,
  isScrubbing: boolean,
  playbackConfig?: PlaybackWindowConfig,
  scrubbingConfig?: ScrubbingWindowConfig,
): WindowResult {
  if (isScrubbing) {
    return collectScrubbingWindow(allElements, currentTime, scrubbingConfig);
  }
  return collectPlaybackWindow(allElements, currentTime, playbackConfig);
}

// ---------------------------------------------------------------------------
// Composition child filtering
// ---------------------------------------------------------------------------

/**
 * Returns true if the element is a composition whose children are ALL video
 * elements (i.e. a layout split-scene composition).
 */
function isAllVideoComposition(el: Record<string, any>): boolean {
  return (
    el.type === 'composition' &&
    Array.isArray(el.elements) &&
    el.elements.length > 0 &&
    el.elements.every((child: Record<string, any>) => child.type === 'video')
  );
}

/**
 * Resolve a composition child's effective duration in seconds.
 *
 * A child element with no explicit `duration` inherits the remainder of its
 * parent composition's duration (Creatomate semantics): it plays from its
 * relative start time to the end of the composition. Intro/end card
 * compositions rely on this — the duration lives on the composition while the
 * inner video element has `duration: null`. Without this fallback the child is
 * treated as a zero-length blip and gets filtered out of every window.
 *
 * Returns 0 only when neither the child nor the composition specify a usable
 * duration (matching the previous behaviour for that unknown case).
 */
function resolveChildDuration(
  composition: Record<string, any>,
  child: Record<string, any>,
): number {
  const explicit = parseFloat(child.duration);
  if (Number.isFinite(explicit)) return explicit;

  const compDuration = parseFloat(composition.duration);
  if (!Number.isFinite(compDuration)) return 0;

  const childTime = parseFloat(child.time) || 0;
  return Math.max(0, compDuration - childTime);
}

/**
 * Absolute time range of a composition child, in composition-timeline seconds.
 *
 * A child's `time` is **relative to its composition's start**, so its absolute
 * start is `composition.time + child.time`. Duration falls back to the
 * composition's remaining span when the child has none (see
 * {@link resolveChildDuration}). Single source of truth for both windowing
 * paths so they can't diverge.
 */
function resolveChildAbsoluteRange(
  composition: Record<string, any>,
  child: Record<string, any>,
): { start: number; duration: number } {
  const start =
    (parseFloat(composition.time) || 0) + (parseFloat(child.time) || 0);
  return { start, duration: resolveChildDuration(composition, child) };
}

/**
 * For all-video compositions, filter out child elements whose absolute time
 * range falls entirely outside `[timeA, timeB]`.
 *
 * Child element times are **relative to the composition's start**, so their
 * absolute start is `composition.time + child.time`.
 */
function filterCompositionChildren(
  elements: Record<string, any>[],
  timeA: number,
  timeB: number,
): Record<string, any>[] {
  return elements.map(el => {
    if (!isAllVideoComposition(el)) return el;

    const filtered = el.elements.filter((child: Record<string, any>) => {
      const { start, duration } = resolveChildAbsoluteRange(el, child);
      // Keep if overlaps [timeA, timeB]
      return start < timeB && start + duration > timeA;
    });

    return { ...el, elements: filtered };
  });
}

/**
 * Expand all-video compositions into virtual entries representing each child
 * with its absolute time, so budget counting accounts for individual children.
 * Non-composition elements pass through as-is. Each entry carries a
 * `_sourceIndex` to trace back to the original element.
 */
interface ExpandedEntry {
  time: number;
  duration: number;
  /** Element type (e.g. 'video', 'image', 'audio'); drives budget cost. */
  type: string;
  _sourceIndex: number;
}

function expandForBudgetCounting(
  elements: Record<string, any>[],
): ExpandedEntry[] {
  const result: ExpandedEntry[] = [];
  for (let idx = 0; idx < elements.length; idx++) {
    const el = elements[idx];
    if (isAllVideoComposition(el)) {
      for (const child of el.elements) {
        const { start, duration } = resolveChildAbsoluteRange(el, child);
        result.push({
          time: start,
          duration,
          _sourceIndex: idx,
          type: 'video',
        });
      }
    } else {
      result.push({
        time: parseFloat(el.time),
        duration: parseFloat(el.duration),
        type: typeof el.type === 'string' ? el.type : '',
        _sourceIndex: idx,
      });
    }
  }
  return result;
}

// ---------------------------------------------------------------------------
// Playback mode — forward walk with budget + same-time groups
// ---------------------------------------------------------------------------

function collectPlaybackWindow(
  allElements: Record<string, any>[],
  currentTime: number,
  config: PlaybackWindowConfig = DEFAULT_PLAYBACK_CONFIG,
): WindowResult {
  const timeA = currentTime;

  // Expand all-video compositions into individual children for budget counting
  const expanded = expandForBudgetCounting(allElements);
  const sorted = [...expanded].sort((a, b) => a.time - b.time);

  const includedSourceIndices = new Set<number>();
  let totalCost = 0;
  let timeB = timeA;
  let budgetExceeded = false;
  let i = 0;

  while (i < sorted.length) {
    const entry = sorted[i];

    // Skip entries entirely before the playhead
    if (entry.time + entry.duration <= timeA) {
      i++;
      continue;
    }

    // Collect the group of entries sharing the same start time
    const groupTime = entry.time;
    const group: ExpandedEntry[] = [];
    while (i < sorted.length && sorted[i].time === groupTime) {
      if (sorted[i].time + sorted[i].duration > timeA) {
        group.push(sorted[i]);
      }
      i++;
    }

    // All-or-nothing: if adding this group exceeds budget, exclude all
    const groupCost = group.reduce((sum, e) => sum + elementCost(e.type), 0);
    if (totalCost + groupCost > config.maxElements + BUDGET_EPSILON) {
      timeB = Math.max(0, groupTime - 0.0001);
      budgetExceeded = true;
      break;
    }

    totalCost += groupCost;
    for (const g of group) {
      includedSourceIndices.add(g._sourceIndex);
    }
    timeB = groupTime;
  }

  // If all elements fit within budget, no upper bound restriction needed
  if (!budgetExceeded) {
    timeB = Infinity;
  }

  // Collect original elements whose expanded entries were included
  const visibleElements = allElements.filter((_, idx) =>
    includedSourceIndices.has(idx),
  );

  return {
    visibleElements: filterCompositionChildren(visibleElements, timeA, timeB),
    timeA,
    timeB,
  };
}

// ---------------------------------------------------------------------------
// Scrubbing mode — symmetric time window, no budget cap
// ---------------------------------------------------------------------------

function collectScrubbingWindow(
  allElements: Record<string, any>[],
  currentTime: number,
  config: ScrubbingWindowConfig = DEFAULT_SCRUBBING_CONFIG,
): WindowResult {
  const timeA = Math.max(0, currentTime - config.scrubbingRadius);
  const timeB = currentTime + config.scrubbingRadius;

  const visibleElements = allElements.filter(el => {
    const endTime = parseFloat(el.time) + parseFloat(el.duration);
    return parseFloat(el.time) < timeB && endTime > timeA;
  });

  return {
    visibleElements: filterCompositionChildren(visibleElements, timeA, timeB),
    timeA,
    timeB,
  };
}
